import CircleLogo from "@circle-assets/images/logo.svg";
import { CSRFTokenField } from "@circle-react-uikit/CSRFTokenField";
import { Loader } from "@circle-react-uikit/Loader";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import {
  getCommunityIconUrl,
  getCommunityLogoUrl,
} from "@circle-react/helpers/communityHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import { ThemeContext } from "@circle-react/providers/Theme/ThemeContext";
import { useCompassContext } from "@circleco/compass/context/CompassContext";
import { CompassProvider } from "@circleco/compass/context/CompassProvider";
import { map } from "lodash";
import type { ComponentPropsWithoutRef } from "react";
import { useContext, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import { t } from "@/i18n-js/instance";
import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";

import { ErrorTemplateV4 } from "./ErrorTemplateV4";

export interface ErrorTemplateProps {
  [key: string]: any;
  errorCode: number;
  fullHeight?: boolean;
  title?: string;
}

const getDefaultTitle = (errorCode: number, isCircleV4: boolean) => {
  if (errorCode === 404) {
    return t(
      isCircleV4 ? "error_page.v4.page_not_found" : "error_page.page_not_found",
    );
  }

  return t(
    isCircleV4
      ? "error_page.v4.something_went_wrong"
      : "error_page.something_went_wrong",
  );
};

export const ErrorTemplateComponent = ({
  title,
  errorCode,
  fullHeight,
  ...passThroughProps
}: ErrorTemplateProps) => {
  const { currentCommunity, currentUser, isLoading } = usePunditUserContext();
  const { isCircleV4 } = useCircleV4();
  const isDarkAppearance = useContext(ThemeContext)?.isDarkAppearance ?? false;
  const compassContext = useCompassContext();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  if (isLoading) {
    return <Loader center />;
  }

  let communityUrl = "https://circle.so";
  let logoUrl = CircleLogo;
  let v4LogoUrl = "";
  let brandColor: string | undefined;

  if (currentCommunity) {
    communityUrl = window.location.origin;
    logoUrl = getCommunityLogoUrl(currentCommunity);
    v4LogoUrl =
      getCommunityIconUrl(currentCommunity, isDarkAppearance) ||
      getCommunityLogoUrl(currentCommunity) ||
      "";
    brandColor = isDarkAppearance
      ? currentCommunity.prefs.brand_color.dark
      : currentCommunity.prefs.brand_color.light;
  }

  const textareaPlaceholder = currentCommunity?.has_plus_plan
    ? "error_page.message_placeholder_circle_plus"
    : "error_page.message_placeholder";
  const resolvedTitle = title || getDefaultTitle(errorCode, isCircleV4);

  const submitErrorReport = async (form: HTMLFormElement) => {
    try {
      await recaptchaRef?.current?.executeAsync();
    } catch {
      // reCAPTCHA failed, submit anyway. Backend verify_recaptcha will handle validation
    }

    form.submit();
  };

  const handleSubmit: NonNullable<
    ComponentPropsWithoutRef<"form">["onSubmit"]
  > = event => {
    event.preventDefault();
    void submitErrorReport(event.currentTarget);
  };

  if (isCircleV4) {
    const errorTemplateV4 = (
      <ErrorTemplateV4
        title={resolvedTitle}
        errorCode={errorCode}
        currentCommunity={currentCommunity}
        currentUser={currentUser}
        communityUrl={communityUrl}
        fullHeight={fullHeight}
        logoUrl={v4LogoUrl}
        brandColor={brandColor}
        recaptchaRef={recaptchaRef}
        onSubmit={handleSubmit}
        passThroughProps={passThroughProps}
      />
    );

    return compassContext ? (
      errorTemplateV4
    ) : (
      <CompassProvider>{errorTemplateV4}</CompassProvider>
    );
  }

  return (
    <div
      className={anchorClassNames(
        "center-layout error-layout notification-error",
      )}
    >
      <div className={anchorClassNames("center-container")}>
        <div className={anchorClassNames("community__brand")}>
          {logoUrl ? (
            <a href={communityUrl} target="_blank" rel="noopener noreferrer">
              <img
                loading="lazy"
                alt=""
                className={anchorClassNames("logo")}
                src={logoUrl}
              />
            </a>
          ) : (
            <a
              className={classNames(
                anchorClassNames("brand-icon"),
                legacyClassNames("brand-icon__initial"),
              )}
              style={{ backgroundColor: brandColor }}
              target="_blank"
              rel="noopener noreferrer"
              href={communityUrl}
            >
              {currentCommunity?.name?.[0]}
            </a>
          )}
        </div>
        <div className="mb-12">
          <h1>{resolvedTitle}</h1>
        </div>
        <h4 className="mb-6"> {t("error_page.suspect_bug")}</h4>
        <form
          action="/error_reports/send_email_notification"
          acceptCharset="UTF-8"
          method="post"
          onSubmit={handleSubmit}
        >
          <CSRFTokenField />
          <div className={classNames(anchorClassNames("form-group"), "mb-4")}>
            <label
              className={classNames(legacyClassNames("required"), "sr-only")}
              htmlFor="error_detail"
            >
              {t("error_page.error_detail")}
            </label>
            <textarea
              placeholder={t(textareaPlaceholder)}
              rows={7}
              cols={50}
              required
              name="error_detail"
              id="error_detail"
              className={classNames(
                anchorClassNames("form-control"),
                "placeholder:text-light placeholder:opacity-100",
              )}
            />
          </div>
          {window.CAPTCHA_SITE_KEY && (
            <div className="mb-4 flex items-center justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={window.CAPTCHA_SITE_KEY}
                size="invisible"
              />
            </div>
          )}
          <input
            value={errorCode}
            type="hidden"
            name="error_status"
            id="error_status"
          />
          <input
            value={window.location.href}
            type="hidden"
            name="error_url"
            id="error_url"
          />
          <input
            type="submit"
            name="commit"
            value={t("error_page.submit_report")}
            className={anchorClassNames("btn btn-primary")}
            data-disable-with="Submit report"
          />
          {map(passThroughProps, (value, key) => (
            <input key={key} name={key} type="hidden" value={value} />
          ))}
        </form>
      </div>
    </div>
  );
};
